import { Component, OnInit, signal, computed } from '@angular/core';
import { IOpsViewerMessagesService } from '../../services/services';
import { JobIntegration, JobIntegrationClient, VendorJob, CompanyNameViewModel } from '../../services/nswag/nswagclient';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, merge, of, combineLatest } from 'rxjs';
import { switchMap, catchError, tap, startWith, shareReplay, map } from 'rxjs/operators';
import { IApplicationContextService } from '../../services/iApplication-context.service';

@Component({
    selector: 'app-job-integrations',
    templateUrl: './job-integrations.component.html',
    styleUrls: ['./job-integrations.component.css'],
    standalone: false
})
export class JobIntegrationsComponent implements OnInit {
  // Signals for reactive state
  loading = signal(false);
  
  // Subjects for triggering data refresh
  private refreshTrigger$ = new Subject<void>();
  
  // Observables for data streams (to be consumed with async pipe)
  activeIntegrations$: Observable<JobIntegration[]>;
  nonIntegratedJobs$: Observable<VendorJob[]>;

  // Computed signal example - can be used for derived state
  hasIntegrations = computed(() => !this.loading());
  
  // Dialog state
  displayCreateDialog = signal(false);
  selectedJob = signal<VendorJob | null>(null);
  selectedIntegrationCompany = signal<string>('Deep Well');
  availableCompanies = ['Deep Well'];

  constructor(
    private _jobIntegrationClient: JobIntegrationClient,
    private _messageService: IOpsViewerMessagesService,
    private _confirmationService: ConfirmationService,
    private _applicationContextService: IApplicationContextService
  ) {
    // Get current company from application context
    const companyContext$ = this._applicationContextService.companyContextObserver;
    
    // Initialize observables with refresh capability and company filtering
    this.activeIntegrations$ = combineLatest([
      this.refreshTrigger$.pipe(startWith(null)),
      companyContext$
    ]).pipe(
      tap(() => this.loading.set(true)),
      switchMap(([_, company]) => 
        this._jobIntegrationClient.getActiveIntegrations(company?.companyId).pipe(
          catchError(error => {
            this._messageService.errorMessage(error, 'Could not load active integrations');
            return of([]);
          })
        )
      ),
      map(integrations => 
        integrations.sort((a, b) => {
          const dateA = a.created ? new Date(a.created).getTime() : 0;
          const dateB = b.created ? new Date(b.created).getTime() : 0;
          return dateB - dateA; // Most recent first
        })
      ),
      tap(() => this.loading.set(false)),
      shareReplay(1) // Cache the latest value
    );

    this.nonIntegratedJobs$ = combineLatest([
      this.refreshTrigger$.pipe(startWith(null)),
      companyContext$
    ]).pipe(
      switchMap(([_, company]) => 
        this._jobIntegrationClient.getActiveJobsWithoutIntegrations(company?.companyId).pipe(
          catchError(error => {
            this._messageService.errorMessage(error, 'Could not load non-integrated jobs');
            return of([]);
          })
        )
      ),
      map(jobs => 
        jobs.sort((a, b) => {
          const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
          return dateB - dateA; // Most recent first
        })
      ),
      shareReplay(1) // Cache the latest value
    );
  }

  ngOnInit(): void {
    // Data loading is handled by the observables in the constructor
    // No explicit load calls needed - async pipe will trigger subscriptions
  }

  onCreateIntegration(job: VendorJob): void {
    this.selectedJob.set(job);
    this.selectedIntegrationCompany.set('Deep Well');
    this.displayCreateDialog.set(true);
  }
  
  confirmCreateIntegration(): void {
    const job = this.selectedJob();
    if (!job) return;
    
    const newIntegration = JobIntegration.fromJS({
      jobId: job.id,
      integrationCompany: this.selectedIntegrationCompany(),
      integrationType: 'API',
      status: 'Active',
      notes: '',
      created: new Date(),
      modified: new Date()
    });

    this._jobIntegrationClient.create(newIntegration).subscribe(
      result => {
        this._messageService.successMessage('Success', 'Integration created successfully');
        this.displayCreateDialog.set(false);
        this.selectedJob.set(null);
        this.refreshData();
      },
      error => {
        this._messageService.errorMessage(error, 'Could not create integration');
      }
    );
  }
  
  cancelCreateIntegration(): void {
    this.displayCreateDialog.set(false);
    this.selectedJob.set(null);
  }

  onDeleteIntegration(integration: JobIntegration): void {
    this._confirmationService.confirm({
      key: 'confirmationDialog',
      message: `Are you sure you want to delete the integration for "${integration.jobName}" to "${integration.integrationCompany}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (!integration.id) {
          return;
        }
        this._jobIntegrationClient.delete(integration.id).subscribe(
          () => {
            this._messageService.successMessage('Success', 'Integration deleted successfully');
            this.refreshData();
          },
          error => {
            this._messageService.errorMessage(error, 'Could not delete integration');
          }
        );
      }
    });
  }

  refreshData(): void {
    this.refreshTrigger$.next();
  }
}
